import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.jsx';
import { EmptyState } from '../../components/ui/EmptyState.jsx';
import { Skeleton } from '../../components/ui/Skeleton.jsx';
import { showToast } from '../../components/ui/Toaster.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';

import { PriceListModal } from './PriceListModal.jsx';

export function PriceListsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [modalState, setModalState] = useState(null); // null | { priceList: object|null }
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['sales', 'price-lists', 'list'],
    queryFn: () => salesApi.listPriceLists().then((r) => r.items || []),
  });

  const setActive = useMutation({
    mutationFn: ({ id, isActive }) => salesApi.setPriceListActive(id, isActive),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales', 'price-lists'] }),
    onError: (err) => showToast(err?.message || t('common.loadError'), 'danger'),
  });

  const remove = useMutation({
    mutationFn: (id) => salesApi.deletePriceList(id),
    onSuccess: () => {
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ['sales', 'price-lists'] });
    },
    onError: (err) => {
      setDeleteTarget(null);
      showToast(err?.message || t('common.loadError'), 'danger');
    },
  });

  async function openEdit(row) {
    try {
      const detail = await salesApi.getPriceList(row.id).then((r) => r.items);
      setModalState({ priceList: detail });
    } catch (err) {
      showToast(err?.message || t('common.loadError'), 'danger');
    }
  }

  const columns = [
    t('col.name'),
    t('priceList.col.products'),
    t('priceList.col.tiers'),
    t('priceList.col.customers'),
    t('col.status'),
    t('common.actions'),
  ];

  const rows = data || [];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('priceList.title')}</h1>
          <p className="muted">{t('priceList.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setModalState({ priceList: null })}>
            {t('priceList.action.new')}
          </Button>
        </div>
      </header>

      {error && <div className="alert danger">{error.message}</div>}

      {isLoading ? (
        <div className="table-wrap">
          <Skeleton variant="table" />
        </div>
      ) : rows.length === 0 ? (
        <div className="table-wrap">
          <EmptyState message={t('priceList.empty')} />
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {columns.map((label, idx) => (
                  <th key={idx}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td className="num mono">{Number(row.productCount) || 0}</td>
                  <td className="num mono">{Number(row.itemCount) || 0}</td>
                  <td className="num mono">{Number(row.customerCount) || 0}</td>
                  <td>
                    <Badge status={row.isActive ? 'ACTIVE' : 'INACTIVE'} />
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(row)}>
                        {t('priceList.action.edit')}
                      </Button>
                      <Button
                        size="sm"
                        variant="default"
                        disabled={setActive.isPending}
                        onClick={() => setActive.mutate({ id: row.id, isActive: !row.isActive })}
                      >
                        {row.isActive ? t('priceList.action.deactivate') : t('priceList.action.activate')}
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => setDeleteTarget(row)}>
                        {t('priceList.action.delete')}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalState && (
        <PriceListModal
          key={modalState.priceList?.id ?? 'new'}
          open
          priceList={modalState.priceList}
          onClose={() => setModalState(null)}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={t('priceList.action.delete')}
        message={t('priceList.confirmDelete', { count: Number(deleteTarget?.customerCount) || 0 })}
        confirmLabel={t('priceList.action.delete')}
        variant="danger"
        isPending={remove.isPending}
        onConfirm={() => remove.mutate(deleteTarget.id)}
        onClose={() => setDeleteTarget(null)}
      />
    </section>
  );
}
